/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router';
import { Field } from 'react-final-form';
import { isNil, omitBy, pick, get } from 'lodash';
import { FORM_ERROR } from 'final-form';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';

import { Box } from 'grommet';
import { Text } from '@blend/enterprise-components';
import { MeContext } from 'blb-contexts/Me';
import { getUserProfile, updateUserAddress, updateUserProfile } from 'blb-api/users';
import { FormCard } from 'blb-components/FormCard';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { AlertBar as GAlertBar } from 'blb-components/GrommetComponents';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';
import {
  additionalLineTextInputConfig,
  cityInputConfig,
  countyInputConfig,
  getNameInputConfig,
  lineTextInputConfig,
  phoneNumberInputConfig,
  stateInputConfig,
  zipcodeInputConfig,
} from 'blb-inputs/common';
import { InnerContainer, MiddleContainer, Header, OuterContainer } from 'blb-components/IdentityLike';
import { addressFields, optionalAddressFields } from 'blb-utils/address';
import { profileFields } from 'blb-utils/profile';
import { getPersona, isProfilePersona } from 'blb-persona/index';

const ColumnWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;
`;

const AlertBar = styled(GAlertBar)<GAlertBar.Props>`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

type FormValues = BlendBridge.API.UserProfileNoAddress & BlendBridge.API.Address;

export const ExtendedRegistrationPage: React.FC<Record<string, never>> = () => {
  const isMounted = useRef(false);
  const me = useContext(MeContext)!;
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<Partial<FormValues>>({ type: 'WORK' });
  const persona = getPersona(me.principalType);

  const fetchProfile = async () => {
    try {
      const { data } = await getUserProfile(me.principalId);
      const defaultAddress = data.addresses.find(address => address.isDefault);
      // if both the profile data and default address are fully populated, they
      // can skip this page.
      const prepopulatedValues = {
        ...initialValues,
        ...pick(data, profileFields),
        ...pick(defaultAddress, [...addressFields, ...optionalAddressFields]),
      };
      // Check if the component is still mounted before EVERY state setter
      isMounted.current && setInitialValues(prepopulatedValues);
    } catch (err) {
      isMounted.current && setError(errorMessage('PAGE_NOT_PERMITTED', me.principalType));
    } finally {
      isMounted.current && setIsLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchProfile();
    return () => {
      // stop any requests or state setting from happening if the component unmounts
      isMounted.current = false;
    };
  }, [me]);

  const onSubmit = async (formValues: FormValues) => {
    try {
      await updateUserProfile(me.principalId, pick(formValues, profileFields));

      // remove any keys that have undefined/null values but keep false and '' values
      const addressBody = omitBy(pick(formValues, [...addressFields, ...optionalAddressFields]), isNil);
      await updateUserAddress(me.principalId, addressBody);

      // reloading the app so that meContext is refreshed and routes are updated.
      window.location.reload();
    } catch (error) {
      Sentry.captureException(error);
      return {
        [FORM_ERROR]: get(error, 'response.data', errorMessage('SAVING_ADDRESS', me.principalType)),
      };
    }
  };

  if (isLoading) {
    return <CenteredLoadingSpinner />;
  }

  if (error) {
    return <AlertBar errorMessage={error} id={`ExtendedRegistrationPage-error`} />;
  }

  // If not a profile persona, they don't need to register, and they shouldn't
  // be here so send them to their landing page.
  if (!isProfilePersona(persona)) {
    return <Redirect to={persona.getAllowedRoutes(me).defaultLandingPath} />;
  }

  const { headerLabelOverride, lineTextOverride } = persona.getRegisrationFormOverrides();

  return (
    <OuterContainer>
      <MiddleContainer>
        <InnerContainer>
          <Header />
          <FormCard<FormValues>
            headerLabel={headerLabelOverride || <Text>You can edit this information later.</Text>}
            headerTitle="Create your profile"
            id="ExtendedProfile"
            initialValues={initialValues}
            onSubmit={onSubmit}
            showCancel={false}
            submitLabel="Continue"
          >
            <ColumnWrapper>
              <Field {...getNameInputConfig('first')} />
              <Field {...getNameInputConfig('last')} />
            </ColumnWrapper>
            <Field {...phoneNumberInputConfig} />
            <Field {...{ ...lineTextInputConfig, ...lineTextOverride }} />
            <Field {...additionalLineTextInputConfig} />
            <ColumnWrapper>
              <Field {...cityInputConfig} />
              <Field {...countyInputConfig} />
            </ColumnWrapper>
            <ColumnWrapper>
              <Field {...stateInputConfig} />
              <Field {...zipcodeInputConfig} />
            </ColumnWrapper>
          </FormCard>
        </InnerContainer>
      </MiddleContainer>
    </OuterContainer>
  );
};
